
import { useEffect, Suspense } from "react";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { withRenderingDebug, useChildrenValidation } from "@/utils/errorDetector";
import { useAdminCheckQuery } from "@/hooks/useAdminCheckQuery";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { authNavigationController, AuthFlowStage } from "@/controllers/AuthNavigationController";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { useIndexState } from "@/hooks/useIndexState";
import { useNetworkStatus } from "@/hooks/useNetworkStatus";
import { mobileDebugger } from "@/utils/mobileDebugger";
import { useBrandColors } from "@/hooks/useBrandColors";
import { 
  logIndexComponentRendering,
  logIndexAuthFlow,
  logIndexNetworkStatus,
  logIndexTermsCheck,
  logIndexErrorHandling,
  logIndexPerformanceMetrics
} from "@/utils/consoleLogger";

// Static import for HomeContent instead of lazy loading
import { HomeContent } from "@/components/HomePage/HomeContent";

const LoadingOverlay = () => {
  const { brandColors } = useBrandColors();
  
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center">
      <div className="text-center p-4">
        <div className="w-16 h-16 mx-auto mb-4">
          <img 
            src="/lovable-uploads/98a9fcc4-c199-4be7-8bad-6756a3aa8870.png"
            alt="Loading"
            className="w-full h-full object-contain"
            style={{
              filter: brandColors?.color2 ? `hue-rotate(${getHueRotation(brandColors.color2)}deg)` : undefined
            }}
          />
        </div>
        <h2 className="text-xl font-semibold mb-2">Loading...</h2>
        <p className="text-gray-500">Please wait while we prepare your experience</p>
      </div>
    </div>
  );
};

// Helper function to calculate hue rotation for brand color
const getHueRotation = (hexColor: string): number => {
  // Simple hue rotation calculation - this can be enhanced
  const hex = hexColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  return (r + g + b) / 3;
};

// Debug component to display when errors occur
const IndexErrorFallback = ({ error }: { error?: Error }) => (
  <div className="min-h-screen bg-gray-50 flex flex-col">
    <Header
      selectedRegion="nairobi"
      setSelectedRegion={() => {}}
      searchQuery=""
      setSearchQuery={() => {}}
      showSearchAndLocation={false}
    />
    <div className="container mx-auto px-4 py-16 flex-grow">
      <div className="bg-red-50 border border-red-200 rounded-lg p-6 max-w-xl mx-auto">
        <h2 className="text-xl font-semibold text-red-700 mb-4">
          Something went wrong loading the home page
        </h2>
        <p className="mb-4 text-red-600">
          We encountered an error while loading the home page content.
        </p>
        {error && (
          <pre className="bg-white p-4 rounded border border-red-100 text-sm mb-4 overflow-auto">
            {error.message}
          </pre>
        )}
        <button
          onClick={() => window.location.reload()}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded"
        >
          Reload Page
        </button>
      </div>
    </div>
  </div>
);

const Index = () => {
  useChildrenValidation(null, 'Index');
  logIndexComponentRendering('Index component rendering...');
  
  // Add mobile-specific logging
  useEffect(() => {
    console.log('[Index Mobile Debug] Component mounted:', {
      isMobile: window.innerWidth < 768,
      viewport: { width: window.innerWidth, height: window.innerHeight },
      userAgent: navigator.userAgent.substring(0, 100)
    });
    
  }, []);
  
  const { data: isAdmin = false } = useAdminCheckQuery();
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  
  const { 
    state: { search, ui, auth },
    setSearch,
    setUIState,
    setAuthState,
  } = useIndexState();
  
  const networkStatus = useNetworkStatus();

  useEffect(() => {
    logIndexComponentRendering('Index component mounted');
    
    try {
      // Set initial UI state based on network status
      logIndexNetworkStatus('Setting UI state based on network', { type: networkStatus.type });
      setUIState({
        loading: true,
        connectionStatus: networkStatus.type,
      });
      
      const maxLoadingTime = networkStatus.type === 'slow' ? 5000 : 2000;
      const timeoutId = window.setTimeout(() => {
        logIndexPerformanceMetrics('Loading timeout completed', { maxLoadingTime });
        setUIState({
          loading: false,
          contentReady: true,
        });
      }, maxLoadingTime);
      
      return () => {
        clearTimeout(timeoutId);
      };
    } catch (err) {
      logIndexErrorHandling('Error in Index initial setup:', err);
      setUIState({
        loading: false,
        contentReady: false,
      });
    }
  }, [networkStatus.type, setUIState]);

  useEffect(() => {
    const checkForRecoveryToken = () => {
      const params = new URLSearchParams(window.location.search);
      const token = params.get('token');
      const type = params.get('type');
      return { token, type };
    };
    
    const checkForRecoverySession = async () => {
      try {
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          logIndexAuthFlow("Error getting session:", error);
          return false;
        }
        
        if (!data.session) {
          logIndexAuthFlow("No active session found");
          return false;
        }
        
        const resetCompleted = localStorage.getItem('lamulamu_pwd_reset_completed');
        if (resetCompleted) {
          logIndexAuthFlow("Password reset was already completed at:", resetCompleted);
          localStorage.removeItem('lamulamu_pwd_reset_completed');
          return false;
        }
        
        const lastEvent = localStorage.getItem('supabase.auth.event');
        const lastUrl = localStorage.getItem('supabase.auth.url');
        
        if (
          (lastUrl && (lastUrl.includes('type=recovery') || lastUrl.includes('type=recover'))) ||
          (window.location.href && window.location.href.includes('access_token='))
        ) {
          logIndexAuthFlow("Detected recovery flow indicators in session");
          
          authNavigationController.startFlow('recovery', null);
          authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
          
          localStorage.setItem('lamulamu_in_recovery', 'true');
          
          return true;
        }
        
        const inRecovery = localStorage.getItem('lamulamu_in_recovery');
        if (inRecovery === 'true') {
          logIndexAuthFlow("Already in recovery flow, redirecting to reset password");
          return true;
        }
        
        return false;
      } catch (error) {
        logIndexErrorHandling("Error checking for recovery session:", error);
        return false;
      }
    };
    
    const { token, type } = checkForRecoveryToken();
    
    if (token && (type === 'recovery' || type === 'recover')) {
      setAuthState({ recoveryMode: true });
      
      authNavigationController.startFlow('recovery', null, token);
      authNavigationController.updateStage(AuthFlowStage.TOKEN_VERIFIED);
      
      localStorage.setItem('lamulamu_in_recovery', 'true');
      
      const redirectUrl = `/auth?token=${encodeURIComponent(token)}&type=${encodeURIComponent(type)}&view=resetPassword`;
      navigate(redirectUrl, { replace: true });
      return;
    }
    
    checkForRecoverySession().then(isRecoverySession => {
      if (isRecoverySession) {
        setAuthState({ recoveryMode: true });
        navigate('/auth?view=resetPassword', { replace: true });
        return;
      }
      
      checkTermsAcceptance();
    });
    
    const checkTermsAcceptance = async () => {
      if (!isAuthenticated || !user || auth.termsCheckDone) {
        setUIState({ loading: false });
        return;
      }

      try {
        logIndexTermsCheck("Checking if user has accepted ToS");
        
        const { data: currentTerms, error: termsError } = await supabase
          .from('terms_of_service')
          .select('id')
          .eq('is_current', true)
          .single();
          
        if (termsError) {
          logIndexErrorHandling("Error fetching current ToS:", termsError);
          setUIState({ loading: false });
          setAuthState({ termsCheckDone: true });
          return;
        }
        
        if (currentTerms) {
          const { data: profile, error: profileError } = await supabase
            .from('profiles')
            .select('tos_accepted')
            .eq('id', user.id)
            .single();
            
          if (profileError) {
            logIndexErrorHandling("Error fetching user profile:", profileError);
            setUIState({ loading: false });
            setAuthState({ termsCheckDone: true });
            return;
          }
          
          if (profile && !profile.tos_accepted) {
            navigate('/terms-of-service', { state: { forceTermsReview: true } });
            return;
          }
          
          const { data: userTerms, error: userTermsError } = await supabase
            .from('user_terms_acceptance')
            .select('*')
            .eq('user_id', user.id)
            .eq('terms_id', currentTerms.id)
            .maybeSingle();
            
          if (userTermsError) {
            logIndexErrorHandling("Error checking user terms acceptance:", userTermsError);
            setUIState({ loading: false });
            setAuthState({ termsCheckDone: true });
            return;
          }
          
          if (!userTerms) {
            navigate('/terms-of-service', { state: { forceTermsReview: true } });
            return;
          }
        }
      } catch (error) {
        logIndexErrorHandling("Error in ToS check:", error);
      }
      
      setAuthState({ termsCheckDone: true });
      setUIState({ loading: false });
    };
    
    if (!searchParams.get('token') && !window.location.hash.includes('access_token=')) {
      checkTermsAcceptance();
    }
  }, [isAuthenticated, user, navigate, searchParams, location.state, auth.termsCheckDone, setUIState, setAuthState]);

  if (ui.loading && networkStatus.type === "slow") {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header
          selectedRegion={search.region}
          setSelectedRegion={(region) => setSearch({ region })}
          searchQuery=""
          setSearchQuery={(query) => setSearch({ query })}
          showSearchAndLocation={false}
        />
        <LoadingOverlay />
      </div>
    );
  }

  if (ui.loading) {
    return <LoadingOverlay />;
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="flex-grow">
        <Header
          selectedRegion={search.region}
          setSelectedRegion={(region) => setSearch({ region })}
          searchQuery={search.query}
          setSearchQuery={(query) => setSearch({ query })}
          showSearchAndLocation={true}
        />

        <ErrorBoundary 
          componentName="HomeContent"
          fallback={<IndexErrorFallback />}
        >
          <HomeContent 
            searchQuery={search.query}
            selectedRegion={search.region}
          />
        </ErrorBoundary>
      </div>
      <Footer />
    </div>
  );
};

export default Index;
